/**
 * ADAPT Performance Timeline
 *
 * Shared timing utility for all components on the collections page.
 * Every call to perfMark() logs to the browser console showing:
 *   - time since page navigation start (total elapsed)
 *   - time since the previous mark (delta)
 *
 * Usage:
 *   import { perfMark } from '@heke/adapt-core-ui';
 *   perfMark('component:mounted');
 *   perfMark('data:loaded', { count: items.length });
 *
 * In React components:
 *   import { useRenderTimer, useReadyTimer } from '@heke/adapt-core-ui';
 *   useRenderTimer('CollectionsLanding');
 */

import { useEffect, useRef } from "react";

// Capture the moment this module is first evaluated — as early as possible.
// performance.now() is relative to the page navigation start, giving a
// consistent baseline even when this module is loaded lazily.
const _t0: number =
  typeof performance !== "undefined" ? performance.now() : Date.now();

let _prev: number = _t0;

/**
 * Emit a named timing mark to the browser console and the DevTools
 * Performance timeline (via performance.mark).
 */
export function perfMark(
  label: string,
  meta?: Record<string, unknown>,
): void {
  if (typeof performance === "undefined" || typeof console === "undefined")
    return;

  const now = performance.now();
  const total = Math.round(now);
  const delta = Math.round(now - _prev);
  _prev = now;

  const metaStr =
    meta && Object.keys(meta).length > 0
      ? `  ${JSON.stringify(meta)}`
      : "";

  console.log(
    `%c⏱ ADAPT%c  [%c${total}ms%c +${delta}ms]  %c${label}${metaStr}`,
    "color:#7AB600;font-weight:700;font-size:11px",
    "color:#888;font-size:11px",
    "color:#ccc;font-size:11px",
    "color:#888;font-size:11px",
    "color:#fff;font-weight:600;font-size:11px",
  );

  try {
    performance.mark(`adapt:${label}`);
  } catch {
    // ignore — some environments restrict performance.mark
  }
}

/**
 * Returns the total ms elapsed since the module was first evaluated.
 */
export function perfElapsed(): number {
  return typeof performance !== "undefined"
    ? Math.round(performance.now() - _t0)
    : 0;
}

/**
 * React hook — logs mount and unmount of a component to the timeline.
 *
 * @param name  Short component name, e.g. "CollectionsLanding"
 */
export function useRenderTimer(name: string): void {
  const mountedRef = useRef(false);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      perfMark(`${name}:mounted`);
    }
    return () => {
      perfMark(`${name}:unmounted`);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 * React hook — fires once when the component transitions to "ready"
 * (data loaded, first meaningful render complete).
 *
 * @param name     Component name for the log label
 * @param isReady  True when data is loaded and the view is interactive
 */
export function useReadyTimer(name: string, isReady: boolean): void {
  const firedRef = useRef(false);

  useEffect(() => {
    if (isReady && !firedRef.current) {
      firedRef.current = true;
      perfMark(`${name}:ready`, { totalElapsed: perfElapsed() });
    }
  }, [name, isReady]);
}
